import * as React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { ModalBody, ModalFooter } from '@lightspeed/flame/Modal';
import cloneDeep from 'lodash/cloneDeep';
import toInteger from 'lodash/toInteger';
import { useSelector } from 'react-redux';

import {
  HubLabelPrintBody,
  HubLabelPrintError,
  LabelPrintError,
  AdvancedSettings,
} from 'label/HubPrint';
import { getLabelData } from 'label/utils/printer';
import {
  LabelData,
  ApiResponse,
  BARCODE_TYPES,
  LABEL_CONTENT_ACTIVE_OPTIONS,
  ActiveOptions,
  BarcodeTypes,
  BarcodeDisplayOptions,
  LabelContent,
} from 'label/utils/types';
import { PRINTER_STATUSES } from 'root/apps/pages/views/devices/socketClient/types';
import { handlePrintedCheckboxes } from 'root/merchantos/label-print-helper';
import { PrintEvents } from 'root/tracking.types';

import { useLabelPrintForm } from './hooks';
import { StyledModal, StyledModalHeader, StyledText } from './HubLabelPrintBody.style';
import { Header } from './HubLabelPrintHeader';
import { sendLabelToHub } from './sendLabelToHub';

export const MIN_LABELS = 1;
// arbitrary value for max, likely to never be reached by merchant
export const MAX_LABELS = 10000;

export const HubLabelPrintModal: React.FC<{ labelApiUrl: string }> = ({ labelApiUrl }) => {
  const [isOpen, setIsOpen] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(true);
  const [labelData, setLabelData] = React.useState<LabelData[] | undefined>();
  const [activeBarcodeTypes, setActiveBarcodeTypes] = React.useState<typeof BARCODE_TYPES>([]);
  const [activeLabelContentOptions, setActiveLabelContentOptions] = React.useState<
    typeof LABEL_CONTENT_ACTIVE_OPTIONS
  >([]);

  const resetBarcodeType = () => {
    setValue('barcodeType', BarcodeTypes.SYSTEM_SKU);
    setPreviousBarcodeType(BarcodeTypes.SYSTEM_SKU);
  };

  const resetLabelContent = () => {
    setValue('labelContent', labelContentDefault);
    setPreviousLabelContent(labelContentDefault);
  };

  React.useEffect(() => {
    if (labelData) {
      const barcodeTypesCopy = cloneDeep(BARCODE_TYPES);
      const labelContentOptionsCopy = cloneDeep(LABEL_CONTENT_ACTIVE_OPTIONS);

      const filterArray = (
        array: LabelData[],
        filterArray: ActiveOptions[],
        cb: (filterArray: ActiveOptions[]) => void,
        activeDropdownSelection: BarcodeTypes | LabelContent | null,
        reset: () => void
      ) => {
        array.forEach(({ Item }) => {
          filterArray.forEach(option => {
            if (option.active) return;

            if (option.filter(Item)) {
              option.active = true;
            }

            if (activeDropdownSelection && !option.filter(Item)) {
              if (option.value === activeDropdownSelection) {
                reset();
              }
            }
          });
        });
        return cb(filterArray);
      };

      filterArray(
        labelData,
        barcodeTypesCopy,
        setActiveBarcodeTypes,
        barcodeType,
        resetBarcodeType
      );
      filterArray(
        labelData,
        labelContentOptionsCopy,
        setActiveLabelContentOptions,
        labelContent,
        resetLabelContent
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelData]);

  const labelPrinterStatus = useSelector(
    state => state.hub.defaultPrinters.LABEL?.status || PRINTER_STATUSES.DISCONNECTED
  );
  const [isListOfLabels, setIsListOfLabels] = React.useState(false); // When multiple labels are selected, they are returned as a list

  const [error, setError] = React.useState<Error | undefined>();

  const isPrinterError = errorStatuses.includes(labelPrinterStatus);

  const labelPrintForm = useLabelPrintForm({ isLoading, labelData });

  const [showAdvancedSettings, setShowAdvancedSettings] = React.useState<boolean>(false);

  const [isAdvancedSettingsResetting, setIsAdvancedSettingsResettings] = React.useState<boolean>(
    false
  );

  const {
    labelMargins,
    labelQuantity,
    labelSize,
    displayPrice,
    previousLabelSize,
    setPreviousLabelMargin,
    setPreviousLabelSize,
    handleSubmit,
    formState,
    quantity,
    barcodeDisplayOption,
    barcodeType,
    setPreviousBarcodeType,
    labelContent,
    labelContentDefault,
    setPreviousLabelContent,
    dateNow,
    setValue,
  } = labelPrintForm;

  const { isValid } = formState;

  const isDefaultSettings =
    barcodeType === BarcodeTypes.SYSTEM_SKU &&
    barcodeDisplayOption === BarcodeDisplayOptions.BARCODE_AND_NUMBERS &&
    displayPrice &&
    labelContent === labelContentDefault;

  const submit = React.useCallback(() => {
    if (!labelData || !labelSize || !labelQuantity) {
      window.newrelic.noticeError(new Error(LabelPrintError.MissingDataError));
      return;
    }

    sendLabelToHub({
      labelSize,
      previousLabelSize,
      labelData,
      labelMargins,
      labelQuantity,
      isListOfLabels,
      displayPrice,
      barcodeDisplayOption,
      barcodeType,
      labelContent,
      dateNow,
    });
    setPreviousLabelSize(labelSize);
    setPreviousLabelMargin(labelMargins);
    setPreviousLabelContent(labelContent);
    setIsOpen(false);
    handlePrintedCheckboxes(labelData);

    if (!isDefaultSettings) {
      window.dataLayer.push({
        event: PrintEvents.HubPrintLabelPhase1,
      });
    }
  }, [
    labelData,
    labelSize,
    labelQuantity,
    previousLabelSize,
    labelMargins,
    isListOfLabels,
    displayPrice,
    barcodeDisplayOption,
    barcodeType,
    labelContent,
    dateNow,
    setPreviousLabelSize,
    setPreviousLabelMargin,
    setPreviousLabelContent,
    isDefaultSettings,
  ]);

  React.useEffect(() => {
    setIsLoading(true);
    if (!labelApiUrl) {
      setError(new Error(LabelPrintError.ApiError));
      setIsLoading(false);
      return;
    }
    getLabelData(labelApiUrl)
      .then((data?: ApiResponse) => {
        if (!data || !data.Label) {
          setError(new Error(LabelPrintError.ApiError));
          return;
        }
        const isQueue = Array.isArray(data.Label);
        const normalized = (isQueue ? data.Label : [data.Label]) as LabelData[];
        setIsListOfLabels(isQueue);
        setLabelData(normalized);
      })
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [labelApiUrl]);

  const modalContent = (
    <>
      <StyledModalHeader data-test="print-label-header">
        <StyledText>
          <Header
            isLoading={isLoading}
            isListOfLabels={isListOfLabels}
            itemDescription={labelData?.[0].Item.description}
            itemCount={labelData?.length}
            labelQuantity={quantity.labels || getLabelQuantity(labelData)}
          />
        </StyledText>
      </StyledModalHeader>
      <ModalBody scroll>
        {showAdvancedSettings ? (
          <AdvancedSettings
            activeBarcodeTypes={activeBarcodeTypes}
            activeLabelContentOptions={activeLabelContentOptions}
            isLoading={isLoading}
            labelPrintForm={labelPrintForm}
            submit={submit}
            toggleContent={() => setShowAdvancedSettings(false)}
            isDefaultSettings={isDefaultSettings}
            isResetting={isAdvancedSettingsResetting}
            setIsResetting={setIsAdvancedSettingsResettings}
          />
        ) : (
          <HubLabelPrintBody
            isListOfLabels={isListOfLabels}
            isLoading={isLoading}
            labelPrintForm={labelPrintForm}
            submit={submit}
            toggleContent={() => setShowAdvancedSettings(true)}
          />
        )}
      </ModalBody>
    </>
  );

  return (
    <StyledModal
      isOpen={isOpen}
      onRequestClose={() => {
        setIsOpen(false);
      }}
    >
      {error || isPrinterError ? (
        <HubLabelPrintError error={error} labelPrinterStatus={labelPrinterStatus} />
      ) : (
        modalContent
      )}
      <ModalFooter>
        <Flex justifyContent="flex-end" data-test="hub-print-label-modal">
          <Button
            data-test="print-label-cancel-button"
            onClick={() => {
              setIsOpen(false);
            }}
            ml={2}
          >
            Cancel
          </Button>
          {!isPrinterError ? (
            <Button
              data-test="print-label-button"
              onClick={handleSubmit(submit)}
              disabled={!isValid || isLoading || !barcodeType || isAdvancedSettingsResetting}
              loading={isLoading}
              variant="secondary"
              fill
              ml={2}
            >
              Print
            </Button>
          ) : null}
        </Flex>
      </ModalFooter>
    </StyledModal>
  );
};

const errorStatuses: PRINTER_STATUSES[] = [
  PRINTER_STATUSES.ERROR,
  PRINTER_STATUSES.DISCONNECTED,
  PRINTER_STATUSES.NOT_RESPONDING,
  PRINTER_STATUSES.COVER_OPEN,
  PRINTER_STATUSES.NO_PAPER,
];

export function getLabelQuantity(normalized?: LabelData[]): number {
  return normalized?.reduce((total, item) => total + toInteger(item.copies), 0) || 1;
}
